import type { Group, User } from '@gcv/shared';
import { OrganizationRoleResolver } from '@gcv/shared';

import type { SelectOption } from 'ui';

export enum AssessmentAssigneeType {
  User = 'user',
  Group = 'group'
}

export type AssessmentAssigneeValue = string;

export interface AssessmentAssignee {
  type: AssessmentAssigneeType;
  id: string;
}

const UNASSIGNABLE_ROLES = ['bank_examiner'] as const;

export const buildAssigneeValue = (type: AssessmentAssigneeType, id: string): AssessmentAssigneeValue =>
  `${type}:${id}`;

export const parseAssigneeValue = (value?: string): AssessmentAssignee | undefined => {
  if (!value) return undefined;

  const separator = value.indexOf(':');
  if (separator < 1) return undefined;

  const type = value.slice(0, separator);
  const id = value.slice(separator + 1);

  if (!id.length) return undefined;
  if (type !== AssessmentAssigneeType.User && type !== AssessmentAssigneeType.Group) return undefined;

  return { type, id };
};

export const getAssessmentAssigneeOptions = (staff: User[] = [], groups: Group[] = []): SelectOption[] => {
  const roleResolver = new OrganizationRoleResolver();

  const groupOptions = groups
    .filter((group) => !UNASSIGNABLE_ROLES.some((role) => group.roles?.includes(role)))
    .filter((group) => (group.users?.length ?? 0) > 0)
    .map((group) => ({
      value: buildAssigneeValue(AssessmentAssigneeType.Group, group.id),
      label: group.name
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const userOptions = staff
    .filter(
      (user) =>
        user.invitation_status === 'accepted' &&
        !UNASSIGNABLE_ROLES.some((role) => roleResolver.userHasRole(groups, user, role))
    )
    .map((user) => ({
      value: buildAssigneeValue(AssessmentAssigneeType.User, user.id),
      label: `${user.firstName} ${user.lastName}`.trim()
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return [...groupOptions, ...userOptions];
};
